import { useState } from "react";
import {
  AnalisisEstudioPropiedad,
  Propiedad,
  Cliente,
  Usuario,
  ResultadoEstudioROL,
  VerificacionHipotesis,
  EstadoVerificacion,
} from "../../types";
import { Badge } from "../common/Badge";
import { ArbolHipotesisSelector } from "./ArbolHipotesisSelector";
import { findNodoById } from "../../data/arbolHipotesis";

interface AnalisisROLCardProps {
  aep: AnalisisEstudioPropiedad;
  propiedad: Propiedad;
  cliente: Cliente | undefined;
  usuarios: Usuario[];
  onUpdate: (aep: AnalisisEstudioPropiedad) => void;
}

function syncVerificaciones(
  newHipotesisIds: string[],
  currentVerificaciones: VerificacionHipotesis[]
): VerificacionHipotesis[] {
  const kept = currentVerificaciones.filter((v) =>
    newHipotesisIds.includes(v.hipotesisId)
  );
  const existingIds = kept.map((v) => v.hipotesisId);
  const added = newHipotesisIds
    .filter((id) => !existingIds.includes(id))
    .map((id) => ({ hipotesisId: id, estado: "pendiente" as const }));
  return [...kept, ...added];
}

function getHipotesisLabel(id: string): string {
  const nodo = findNodoById(id);
  if (!nodo) return id;
  // Build abbreviated path: parent category > ... > label
  const parts = id.split(".");
  if (parts.length <= 2) return nodo.label;
  // Show category + last parent + leaf
  const parentId = parts.slice(0, -1).join(".");
  const parent = findNodoById(parentId);
  const parentLabel = parent ? parent.label : parts[parts.length - 2];
  return `${parentLabel} > ${nodo.label}`;
}

function getEstadoVerificacionStyle(estado: EstadoVerificacion) {
  switch (estado) {
    case "pendiente":
      return "bg-slate-100 text-slate-600";
    case "en_progreso":
      return "bg-blue-100 text-blue-700";
    case "verificado":
      return "bg-green-100 text-green-700";
    case "no_aplica":
      return "bg-slate-50 text-slate-400";
  }
}

export function AnalisisROLCard({
  aep,
  propiedad,
  cliente,
  usuarios,
  onUpdate,
}: AnalisisROLCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [observaciones, setObservaciones] = useState(aep.observaciones || "");
  const [hipotesis, setHipotesis] = useState<string[]>(aep.hipotesisSeleccionadas || []);
  const [verificaciones, setVerificaciones] = useState<VerificacionHipotesis[]>(
    aep.verificaciones || []
  );
  const [motivoDescarte, setMotivoDescarte] = useState(aep.motivoDescarte || "");
  const [showDescartar, setShowDescartar] = useState(false);

  const asignables = usuarios.filter((u) =>
    ["arquitecto", "abogado", "admin"].includes(u.rol)
  );

  const getResultadoBadge = (resultado: ResultadoEstudioROL) => {
    switch (resultado) {
      case "pendiente": return "neutral" as const;
      case "aprobado_para_solicitud": return "success" as const;
      case "descartado": return "error" as const;
    }
  };

  const getResultadoLabel = (resultado: ResultadoEstudioROL) => {
    switch (resultado) {
      case "pendiente": return "Pendiente";
      case "aprobado_para_solicitud": return "Aprobado";
      case "descartado": return "Descartado";
    }
  };

  const buildAep = (extra: Partial<AnalisisEstudioPropiedad> = {}): AnalisisEstudioPropiedad => ({
    ...aep,
    observaciones: observaciones.trim() || undefined,
    hipotesisSeleccionadas: hipotesis.length > 0 ? hipotesis : undefined,
    verificaciones: verificaciones.length > 0 ? verificaciones : undefined,
    ...extra,
  });

  const handleSaveObservaciones = () => {
    onUpdate(buildAep());
  };

  const handleAprobar = () => {
    onUpdate(buildAep({
      resultado: "aprobado_para_solicitud",
      fechaResultado: new Date().toISOString().split("T")[0],
    }));
  };

  const handleDescartar = () => {
    if (motivoDescarte.trim()) {
      onUpdate(buildAep({
        resultado: "descartado",
        motivoDescarte: motivoDescarte.trim(),
        fechaResultado: new Date().toISOString().split("T")[0],
      }));
      setShowDescartar(false);
    }
  };

  const handleHipotesisChange = (ids: string[]) => {
    setHipotesis(ids);
    const synced = syncVerificaciones(ids, verificaciones);
    setVerificaciones(synced);
    if (!isResolved) {
      onUpdate({
        ...aep,
        hipotesisSeleccionadas: ids.length > 0 ? ids : undefined,
        verificaciones: synced.length > 0 ? synced : undefined,
      });
    }
  };

  const handleVerificacionChange = (
    hipotesisId: string,
    field: keyof VerificacionHipotesis,
    value: string | number | undefined
  ) => {
    const updated = verificaciones.map((v) => {
      if (v.hipotesisId !== hipotesisId) return v;
      const next = { ...v, [field]: value };
      // Auto-set dates
      if (field === "asignadoA" && value) {
        next.fechaAsignacion = next.fechaAsignacion || new Date().toISOString().split("T")[0];
      }
      if (field === "estado" && (value === "verificado" || value === "no_aplica")) {
        next.fechaVerificacion = new Date().toISOString().split("T")[0];
      }
      if (field === "estado" && (value === "pendiente" || value === "en_progreso")) {
        next.fechaVerificacion = undefined;
      }
      return next;
    });
    setVerificaciones(updated);
  };

  const isResolved = aep.resultado !== "pendiente";

  // Progress stats
  const completadas = verificaciones.filter(
    (v) => v.estado === "verificado" || v.estado === "no_aplica"
  ).length;
  const totalVerif = verificaciones.length;

  return (
    <div className="bg-white rounded-lg border border-slate-200">
      {/* Header */}
      <div
        className="p-3 cursor-pointer flex justify-between items-center"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span className="font-medium text-sm text-slate-800">
            ROL {propiedad.rol}
          </span>
          <span className="text-sm text-slate-500">{propiedad.comuna}</span>
          {cliente && (
            <span className="text-xs text-slate-400">{cliente.nombre}</span>
          )}
          <Badge variant={getResultadoBadge(aep.resultado)}>
            {getResultadoLabel(aep.resultado)}
          </Badge>
          {totalVerif > 0 && (
            <span className="text-xs text-slate-400">
              {completadas}/{totalVerif} verif.
            </span>
          )}
        </div>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-slate-200 p-3 space-y-3">
          {/* Hipotesis selector */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Hipotesis
            </label>
            <ArbolHipotesisSelector
              selectedIds={hipotesis}
              onChange={handleHipotesisChange}
            />
          </div>

          {/* Verificaciones checklist */}
          {verificaciones.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-slate-600">
                  Verificaciones ({completadas}/{totalVerif})
                </label>
                {totalVerif > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full transition-all"
                        style={{ width: `${(completadas / totalVerif) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                {verificaciones.map((v) => {
                  const nodo = findNodoById(v.hipotesisId);
                  const docs = nodo?.documentosRecomendados || [];
                  return (
                    <div
                      key={v.hipotesisId}
                      className={`rounded-lg border border-slate-100 p-2 ${
                        v.estado === "no_aplica" ? "opacity-50" : ""
                      }`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Estado */}
                        <select
                          value={v.estado}
                          onChange={(e) =>
                            handleVerificacionChange(v.hipotesisId, "estado", e.target.value as EstadoVerificacion)
                          }
                          disabled={isResolved}
                          className={`text-xs px-1.5 py-0.5 rounded border-0 font-medium ${getEstadoVerificacionStyle(v.estado)} disabled:opacity-60`}
                        >
                          <option value="pendiente">Pendiente</option>
                          <option value="en_progreso">En progreso</option>
                          <option value="verificado">Verificado</option>
                          <option value="no_aplica">No aplica</option>
                        </select>

                        {/* Label */}
                        <span className="text-xs text-slate-700 font-medium flex-1 min-w-0">
                          {getHipotesisLabel(v.hipotesisId)}
                        </span>

                        {/* Docs badge */}
                        {docs.length > 0 && (
                          <span
                            className="text-[10px] text-slate-400 cursor-help"
                            title={`Docs: ${docs.join(", ")}`}
                          >
                            {docs.length} doc{docs.length > 1 ? "s" : ""}
                          </span>
                        )}

                        {/* Asignado a */}
                        <select
                          value={v.asignadoA || ""}
                          onChange={(e) =>
                            handleVerificacionChange(
                              v.hipotesisId,
                              "asignadoA",
                              e.target.value ? Number(e.target.value) : undefined
                            )
                          }
                          disabled={isResolved}
                          className="text-xs px-1.5 py-0.5 border border-slate-200 rounded bg-white text-slate-600 disabled:opacity-60 max-w-[120px]"
                        >
                          <option value="">Sin asignar</option>
                          {asignables.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.nombre}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Observaciones inline */}
                      <div className="mt-1">
                        <input
                          type="text"
                          value={v.observaciones || ""}
                          onChange={(e) =>
                            handleVerificacionChange(v.hipotesisId, "observaciones", e.target.value || undefined)
                          }
                          disabled={isResolved}
                          placeholder="Observaciones..."
                          className="w-full text-xs px-2 py-1 border border-slate-100 rounded bg-slate-50 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:opacity-60 placeholder:text-slate-300"
                        />
                      </div>

                      {/* Fecha info */}
                      {v.fechaVerificacion && (
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {v.estado === "verificado" ? "Verificado" : "Marcado"} el{" "}
                          {new Date(v.fechaVerificacion).toLocaleDateString("es-CL")}
                          {v.asignadoA && ` por ${usuarios.find((u) => u.id === v.asignadoA)?.nombre || "-"}`}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Observaciones */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Observaciones
            </label>
            <textarea
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              disabled={isResolved}
              rows={2}
              className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:bg-slate-50"
            />
          </div>

          {/* Result info for resolved */}
          {isResolved && (
            <div className={`p-2 rounded-lg text-sm ${
              aep.resultado === "aprobado_para_solicitud"
                ? "bg-green-50 text-green-700"
                : "bg-red-50 text-red-700"
            }`}>
              {aep.resultado === "aprobado_para_solicitud"
                ? "Aprobado para solicitud"
                : `Descartado: ${aep.motivoDescarte || "-"}`}
              {aep.fechaResultado && (
                <span className="ml-2 text-xs opacity-70">
                  ({new Date(aep.fechaResultado).toLocaleDateString("es-CL")})
                </span>
              )}
            </div>
          )}

          {/* Action buttons (only for pending) */}
          {!isResolved && (
            <div className="flex gap-2 pt-1">
              <button
                onClick={handleSaveObservaciones}
                className="text-xs px-3 py-1 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
              >
                Guardar
              </button>
              <button
                onClick={handleAprobar}
                className="text-xs px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Aprobar para Solicitud
              </button>
              <button
                onClick={() => setShowDescartar(true)}
                className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
              >
                Descartar
              </button>
            </div>
          )}

          {/* Descartar form */}
          {showDescartar && (
            <div className="bg-red-50 rounded-lg p-2 space-y-2">
              <label className="block text-xs font-medium text-red-700">
                Motivo del descarte *
              </label>
              <textarea
                value={motivoDescarte}
                onChange={(e) => setMotivoDescarte(e.target.value)}
                rows={2}
                placeholder="Razon por la cual se descarta este ROL..."
                className="w-full px-3 py-1.5 border border-red-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowDescartar(false)}
                  className="text-xs px-2 py-1 text-slate-600"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDescartar}
                  disabled={!motivoDescarte.trim()}
                  className="text-xs px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-slate-300"
                >
                  Confirmar Descarte
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
